import React from 'react';
import { ListSubheader, TextField, InputAdornment, Grid, Checkbox, ListItemText, Tooltip } from '@mui/material';
import { Search, SearchOutlined } from "@mui/icons-material";
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import { useTranslation } from 'react-i18next';

// Date
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import 'moment/locale/es';
import moment from "moment";

import { ContainerInputsDate, CustomSelectDemo, CustomButtonV2 } from '../../../pages/styles';
import {
    CustomFormControl,
    CustomInputLabel,
    CustomBadge,
    CustomMenuItem,
    CleanIcon
} from './style';

export const FilterIncidents = ({
    filtroDesde,
    setFiltroDesde,
    filtroHasta,
    setFiltroHasta,
    searchEstacion,
    setSearchEstacion,
    estacion,
    setEstacion,
    isAutocompleteOpen,
    setIsAutocompleteOpen,
    filteredEstaciones,
    filtroCategoria,
    setFiltroCategoria,
    filtroSeveridad,
    setFiltroSeveridad,
    handleAplicarFiltros,
    handleClearFilters,
    handleDownloadPDF
}) => {
    const { t } = useTranslation();

    const MenuPropsSelect = (needAuto) => ({
        autoFocus: false,
        PaperProps: {
            style: {
                height: 'auto',
                maxHeight: '50vh',
                width: needAuto ? 'auto' : 250,
            },
            sx: {
                '&::-webkit-scrollbar': {
                    width: '5px',
                },
            }
        },
    });

    const CATEGORIAS = ['Operacional', 'HSE', 'Calidad'];
    const SEVERIDADES = ['Crítica', 'Alta', 'Media', 'Baja'];
    const ESTACIONES = filteredEstaciones || [];

    const handleSelectChange = (event, setter, allOptions) => {
        const { target: { value } } = event;
        let newValue = typeof value === 'string' ? value.split(',') : value;
        if (newValue.includes('all')) {
            if (newValue.length === allOptions.length + 1) {
                // If "all" was added but we already had all options, deselect all
                setter([]);
            } else {
                // Select all
                setter(allOptions);
            }
        } else {
            setter(newValue);
        }
    };

    return (
        <Grid container spacing={1} justifyContent="flex-end" columns={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>

            {/* Input desde */}
            <Grid item xs={6} sm={3} md={2} xl={2}>
                <ContainerInputsDate sx={{ '& .MuiStack-root > .MuiTextField-root': { minWidth: '100%', width: '100%' } }}>
                    <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale="es">
                        <DemoContainer components={['DatePicker']}>
                            <DatePicker
                                label="Desde"
                                value={filtroDesde ? moment(filtroDesde) : null}
                                onChange={(e) => setFiltroDesde(e ? e.format('YYYY-MM-DD') : '')}
                                slotProps={{ textField: { size: 'small' } }}
                                format="DD/MM/YYYY"
                            />
                        </DemoContainer>
                    </LocalizationProvider>
                </ContainerInputsDate>
            </Grid>

            {/* Input hasta */}
            <Grid item xs={6} sm={3} md={2} xl={2}>
                <ContainerInputsDate sx={{ '& .MuiStack-root > .MuiTextField-root': { minWidth: '100%', width: '100%' } }}>
                    <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale="es">
                        <DemoContainer components={['DatePicker']}>
                            <DatePicker
                                label="Hasta"
                                value={filtroHasta ? moment(filtroHasta) : null}
                                onChange={(e) => setFiltroHasta(e ? e.format('YYYY-MM-DD') : '')}
                                slotProps={{ textField: { size: 'small' } }}
                                format="DD/MM/YYYY"
                            />
                        </DemoContainer>
                    </LocalizationProvider>
                </ContainerInputsDate>
            </Grid>

            {/* Select Estacion */}
            <Grid item xs={6} sm={3} md={2} xl={2}>
                <CustomFormControl>
                    <CustomInputLabel id="filter-estacion">Estación / Planta</CustomInputLabel>
                    <CustomBadge badgeContent={estacion?.length} color="primary">
                        <CustomSelectDemo
                            labelId="filter-estacion"
                            multiple
                            value={estacion || []}
                            onChange={(e) => handleSelectChange(e, setEstacion, ESTACIONES)}
                            onClose={() => setSearchEstacion("")}
                            renderValue={() => ""}
                            MenuProps={MenuPropsSelect(true)}
                        >
                            <ListSubheader>
                                <TextField
                                    size="small"
                                    autoFocus
                                    placeholder={t("common.search")}
                                    fullWidth
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start"><Search /></InputAdornment>
                                    }}
                                    onChange={(e) => setSearchEstacion(e.target.value)}
                                    onKeyDown={(e) => { if (e.key !== "Escape") e.stopPropagation(); }}
                                />
                            </ListSubheader>
                            <CustomMenuItem value="all">
                                <Checkbox checked={estacion?.length === ESTACIONES.length && ESTACIONES.length > 0}
                                    indeterminate={estacion?.length > 0 && estacion?.length < ESTACIONES.length}
                                    sx={{ width: "30px", height: "30px" }} />
                                <ListItemText primary={t('common.selectAll')} />
                            </CustomMenuItem>
                            {ESTACIONES.map((tag) => (
                                <CustomMenuItem key={tag} value={tag} sx={{ fontSize: "0.6em" }}>
                                    <Checkbox checked={estacion?.indexOf(tag) > -1} sx={{ width: "30px", height: "30px" }} />
                                    <ListItemText primary={tag} />
                                </CustomMenuItem>
                            ))}
                        </CustomSelectDemo>
                    </CustomBadge>
                </CustomFormControl>
            </Grid>

            {/* Select Categoria */}
            <Grid item xs={6} sm={3} md={1} xl={1}>
                <CustomFormControl>
                    <CustomInputLabel id="filter-categoria">Categoría</CustomInputLabel>
                    <CustomBadge badgeContent={filtroCategoria?.length} color="primary">
                        <CustomSelectDemo
                            labelId="filter-categoria"
                            multiple
                            value={filtroCategoria || []}
                            onChange={(e) => handleSelectChange(e, setFiltroCategoria, CATEGORIAS)}
                            renderValue={() => ""}
                            MenuProps={MenuPropsSelect(false)}
                        >
                            <CustomMenuItem value="all">
                                <Checkbox checked={filtroCategoria?.length === CATEGORIAS.length}
                                    indeterminate={filtroCategoria?.length > 0 && filtroCategoria?.length < CATEGORIAS.length}
                                    sx={{ width: "30px", height: "30px" }} />
                                <ListItemText primary={t('common.selectAll')} />
                            </CustomMenuItem>
                            {CATEGORIAS.map((cat) => (
                                <CustomMenuItem key={cat} value={cat} sx={{ fontSize: "0.6em" }}>
                                    <Checkbox checked={filtroCategoria?.indexOf(cat) > -1} sx={{ width: "30px", height: "30px" }} />
                                    <ListItemText primary={cat} />
                                </CustomMenuItem>
                            ))}
                        </CustomSelectDemo>
                    </CustomBadge>
                </CustomFormControl>
            </Grid>

            {/* Select Severidad */}
            <Grid item xs={6} sm={3} md={1} xl={1}>
                <CustomFormControl>
                    <CustomInputLabel id="filter-severidad">Severidad</CustomInputLabel>
                    <CustomBadge badgeContent={filtroSeveridad?.length} color="primary">
                        <CustomSelectDemo
                            labelId="filter-severidad"
                            multiple
                            value={filtroSeveridad || []}
                            onChange={(e) => handleSelectChange(e, setFiltroSeveridad, SEVERIDADES)}
                            renderValue={() => ""}
                            MenuProps={MenuPropsSelect(false)}
                        >
                            <CustomMenuItem value="all">
                                <Checkbox checked={filtroSeveridad?.length === SEVERIDADES.length}
                                    indeterminate={filtroSeveridad?.length > 0 && filtroSeveridad?.length < SEVERIDADES.length}
                                    sx={{ width: "30px", height: "30px" }} />
                                <ListItemText primary={t('common.selectAll')} />
                            </CustomMenuItem>
                            {SEVERIDADES.map((sev) => (
                                <CustomMenuItem key={sev} value={sev} sx={{ fontSize: "0.6em" }}>
                                    <Checkbox checked={filtroSeveridad?.indexOf(sev) > -1} sx={{ width: "30px", height: "30px" }} />
                                    <ListItemText primary={sev} />
                                </CustomMenuItem>
                            ))}
                        </CustomSelectDemo>
                    </CustomBadge>
                </CustomFormControl>
            </Grid>

            {/* Botones */}
            <Grid item xs={6} sm={2} md={1} xl={1}>
                <CustomButtonV2 btnType="primary" onClick={handleAplicarFiltros} style={{ minWidth: '40px', width: '100%', padding: '6px 8px' }}>
                    <SearchOutlined />
                </CustomButtonV2>
            </Grid>

            <Grid item xs={6} sm={2} md={1} xl={1}>
                <CustomButtonV2 aria-label="plot" onClick={handleClearFilters} btnType="clean" style={{ minWidth: '40px', width: '100%', padding: '6px 8px' }}>
                    <CleanIcon />
                </CustomButtonV2>
            </Grid>

            {/* Descargar PDF */}
            <Grid item xs={6} sm={2} md={1} xl={1}>
                <Tooltip title="Descargar PDF">
                    <div>
                        <CustomButtonV2 onClick={handleDownloadPDF} style={{ minWidth: '40px', width: '100%', padding: '6px 8px' }}>
                            <PictureAsPdfOutlinedIcon />
                        </CustomButtonV2>
                    </div>
                </Tooltip>
            </Grid>

        </Grid>
    );
};
